import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuthStore } from '../stores/auth'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { DateRangeField } from '../components/ui/DateField'

interface ReconciliationRun {
  id: number
  type: string
  channel: string | null
  periodStart: string
  periodEnd: string
  startedAt: string
  finishedAt: string | null
  totalChecked: number
  matchedOk: number
  mismatches: number
  trigger: string
}

interface ReconciliationResult {
  id: number
  runId: number
  status: string
  sourceType: string
  sourceRef: string
  sourceDate: string | null
  eaRef: string | null
  diffSummary: string | null
  severity: string
  resolved: boolean
  resolvedAt: string | null
  notes: string | null
  createdAt: string
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  ok: { label: 'OK', color: 'bg-b4l-green-light text-b4l-green-dark' },
  missing_in_ea: { label: 'Lipsește în EA', color: 'bg-b4l-red-light text-b4l-red-dark' },
  missing_in_source: { label: 'Lipsește în SPV', color: 'bg-b4l-amber-light text-b4l-amber-dark' },
  value_mismatch: { label: 'Diferență valoare', color: 'bg-b4l-amber-light text-b4l-amber-dark' },
  partner_mismatch: { label: 'CIF diferit', color: 'bg-b4l-amber-light text-b4l-amber-dark' },
}

const SEVERITY_DOT: Record<string, string> = {
  info: 'bg-b4l-text-muted',
  warn: 'bg-b4l-amber-dark',
  critical: 'bg-b4l-red-dark',
}

export function CompliancePage() {
  const navigate = useNavigate()
  const permissions = useAuthStore((s) => s.permissions)
  const canRun = permissions.includes('compliance:run')

  const today = new Date()
  const monthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1)
  const [periodStart, setPeriodStart] = useState(monthStart.toISOString().slice(0, 10))
  const [periodEnd, setPeriodEnd] = useState(today.toISOString().slice(0, 10))

  const runsQuery = useQuery<{ runs: ReconciliationRun[] }>({
    queryKey: ['compliance-runs', 'spv_ea'],
    queryFn: async () =>
      (await api.get<{ runs: ReconciliationRun[] }>('/compliance/runs?type=spv_ea&limit=20')).data,
    refetchInterval: 30_000,
  })

  const runMutation = useMutation({
    mutationFn: async () =>
      (
        await api.post('/compliance/run', {
          type: 'spv_ea',
          periodStart,
          periodEnd,
        })
      ).data,
    onSuccess: (d: unknown) => {
      const r = d as {
        runId: number
        queued?: boolean
        matchedOk?: number
        mismatches?: number
      }
      if (r.queued) {
        toast.success(
          `Rularea #${r.runId} a pornit. Reconcilierea se face în fundal — reîncarcă pagina de detalii în 30–60 de secunde.`,
        )
      } else {
        toast.success(
          `Rularea #${r.runId}: ${r.matchedOk ?? 0} potrivite, ${r.mismatches ?? 0} diferențe`,
        )
      }
      runsQuery.refetch()
      navigate(`/compliance/spv-ea/runs/${r.runId}`)
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare'
      toast.error(message)
    },
  })

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">Reconciliere SPV ↔ EA</h1>

      <Card title="Rulează o reconciliere manuală">
        <div className="space-y-3">
          <DateRangeField
            fromValue={periodStart}
            toValue={periodEnd}
            onChange={(f, t) => {
              setPeriodStart(f)
              setPeriodEnd(t)
            }}
          />
          {canRun && (
            <Button onClick={() => runMutation.mutate()} loading={runMutation.isPending}>
              Rulează
            </Button>
          )}
        </div>
      </Card>

      <Card title="Istoric rulări">
        {runsQuery.isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
        {runsQuery.data?.runs?.length === 0 && (
          <p className="text-sm text-b4l-text-muted">
            Nicio rulare încă. Alege perioada și apasă „Rulează”.
          </p>
        )}
        {runsQuery.data?.runs && runsQuery.data.runs.length > 0 && (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-xs">
                <thead className="border-b border-b4l-border text-left text-b4l-text-muted">
                  <tr>
                    <th className="py-2 pr-3">ID</th>
                    <th className="py-2 pr-3">Perioadă</th>
                    <th className="py-2 pr-3">Pornită de</th>
                    <th className="py-2 pr-3">Verificate</th>
                    <th className="py-2 pr-3">OK</th>
                    <th className="py-2 pr-3">Diferențe</th>
                    <th className="py-2 pr-3">Pornit</th>
                    <th className="py-2 pr-3">Finalizat</th>
                  </tr>
                </thead>
                <tbody>
                  {runsQuery.data.runs.map((r) => (
                    <tr
                      key={r.id}
                      className="cursor-pointer border-b border-b4l-border hover:bg-b4l-bg-soft"
                      onClick={() => navigate(`/compliance/spv-ea/runs/${r.id}`)}
                    >
                      <td className="py-2 pr-3 font-mono">#{r.id}</td>
                      <td className="py-2 pr-3">
                        {new Date(r.periodStart).toLocaleDateString('en-GB')} -{' '}
                        {new Date(r.periodEnd).toLocaleDateString('en-GB')}
                      </td>
                      <td className="py-2 pr-3">{r.trigger}</td>
                      <td className="py-2 pr-3">{r.totalChecked}</td>
                      <td className="py-2 pr-3 text-b4l-green-dark">{r.matchedOk}</td>
                      <td className="py-2 pr-3 text-b4l-red-dark">{r.mismatches}</td>
                      <td className="py-2 pr-3">{new Date(r.startedAt).toLocaleString('en-GB')}</td>
                      <td className="py-2 pr-3">
                        {r.finishedAt ? new Date(r.finishedAt).toLocaleString('en-GB') : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="space-y-2 md:hidden">
              {runsQuery.data.runs.map((r) => (
                <li
                  key={r.id}
                  onClick={() => navigate(`/compliance/spv-ea/runs/${r.id}`)}
                  className="cursor-pointer rounded border border-b4l-border bg-b4l-bg-soft p-3 text-xs"
                >
                  <div className="mb-1 flex items-center justify-between">
                    <span className="font-mono font-semibold">#{r.id}</span>
                    <span className="text-[10px] text-b4l-text-muted">{r.trigger}</span>
                  </div>
                  <div className="mb-2 text-b4l-text-muted">
                    {new Date(r.periodStart).toLocaleDateString('en-GB')} —{' '}
                    {new Date(r.periodEnd).toLocaleDateString('en-GB')}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-[10px] text-b4l-text-muted">Verif.</div>
                      <div className="font-semibold">{r.totalChecked}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-b4l-text-muted">OK</div>
                      <div className="font-semibold text-b4l-green-dark">{r.matchedOk}</div>
                    </div>
                    <div>
                      <div className="text-[10px] text-b4l-text-muted">Dif.</div>
                      <div className="font-semibold text-b4l-red-dark">{r.mismatches}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-[10px] text-b4l-text-muted">
                    Pornit: {new Date(r.startedAt).toLocaleString('en-GB')}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>
    </div>
  )
}

export function ComplianceRunDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canRun = permissions.includes('compliance:run')
  const [statusFilter, setStatusFilter] = useState<string>('')

  const { data, isLoading } = useQuery<{
    run: ReconciliationRun
    results: ReconciliationResult[]
    summary: Record<string, number>
  }>({
    queryKey: ['compliance-run-detail', id, statusFilter],
    queryFn: async () =>
      (
        await api.get(
          `/compliance/runs/${id}/results${statusFilter ? `?status=${statusFilter}` : ''}`,
        )
      ).data,
    enabled: !!id,
    // Auto-refresh la fiecare 5 sec daca run-ul nu e inca finalizat (background)
    refetchInterval: (q) => (q.state.data?.run?.finishedAt ? false : 5000),
  })

  const resolveMutation = useMutation({
    mutationFn: async (resultId: number) =>
      (await api.patch(`/compliance/results/${resultId}/resolve`, {})).data,
    onSuccess: () => {
      toast.success('Marcat rezolvat')
      queryClient.invalidateQueries({ queryKey: ['compliance-run-detail', id] })
    },
  })

  const summary = data?.summary ?? {}

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 md:gap-3">
        <Button variant="secondary" onClick={() => navigate('/compliance/spv-ea')}>
          ← Înapoi
        </Button>
        <h1 className="text-base font-semibold sm:text-xl">
          Rularea #{id} {data?.run ? `(${data.run.type})` : ''}
        </h1>
      </div>

      {data?.run && (
        <Card>
          <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <div>
              <div className="text-xs text-b4l-text-muted">Perioadă</div>
              <div>
                {new Date(data.run.periodStart).toLocaleDateString('en-GB')} —{' '}
                {new Date(data.run.periodEnd).toLocaleDateString('en-GB')}
              </div>
            </div>
            <div>
              <div className="text-xs text-b4l-text-muted">Verificate</div>
              <div className="font-semibold">{data.run.totalChecked}</div>
            </div>
            <div>
              <div className="text-xs text-b4l-text-muted">OK</div>
              <div className="font-semibold text-b4l-green-dark">{data.run.matchedOk}</div>
            </div>
            <div>
              <div className="text-xs text-b4l-text-muted">Diferențe</div>
              <div className="font-semibold text-b4l-red-dark">{data.run.mismatches}</div>
            </div>
          </div>
        </Card>
      )}

      <Card title="Filtre">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setStatusFilter('')}
            className={`rounded border px-3 py-1 text-xs ${
              !statusFilter
                ? 'border-b4l-text bg-b4l-text text-white'
                : 'border-b4l-border bg-white'
            }`}
          >
            Toate ({Object.values(summary).reduce<number>((a, b) => a + b, 0)})
          </button>
          {Object.entries(STATUS_LABELS).map(([k, info]) => (
            <button
              key={k}
              onClick={() => setStatusFilter(k)}
              className={`rounded border px-3 py-1 text-xs ${
                statusFilter === k
                  ? 'border-b4l-text bg-b4l-text text-white'
                  : 'border-b4l-border bg-white'
              }`}
            >
              {info.label} ({summary[k] ?? 0})
            </button>
          ))}
        </div>
      </Card>

      <Card title="Rezultate">
        {isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
        {data?.results?.length === 0 && (
          <p className="text-sm text-b4l-text-muted">Nicio înregistrare pentru filtrul curent.</p>
        )}
        {data?.results && data.results.length > 0 && (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-xs">
                <thead className="border-b border-b4l-border text-left text-b4l-text-muted">
                  <tr>
                    <th className="py-2 pr-3">!</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Sursă</th>
                    <th className="py-2 pr-3">Doc SPV</th>
                    <th className="py-2 pr-3">EA ref</th>
                    <th className="py-2 pr-3">Detalii</th>
                    <th className="py-2 pr-3">Rezolvat</th>
                    <th className="py-2 pr-3">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((r) => {
                    const info = STATUS_LABELS[r.status] ?? {
                      label: r.status,
                      color: 'bg-b4l-bg-soft',
                    }
                    return (
                      <tr key={r.id} className="border-b border-b4l-border">
                        <td className="py-2 pr-3">
                          <span
                            className={`inline-block h-2 w-2 rounded-full ${
                              SEVERITY_DOT[r.severity] ?? 'bg-b4l-text-muted'
                            }`}
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <span className={`rounded px-2 py-0.5 ${info.color}`}>{info.label}</span>
                        </td>
                        <td className="py-2 pr-3 uppercase">{r.sourceType}</td>
                        <td className="py-2 pr-3 font-mono">{r.sourceRef}</td>
                        <td className="py-2 pr-3 font-mono">{r.eaRef ?? '—'}</td>
                        <td className="py-2 pr-3">{r.diffSummary ?? '—'}</td>
                        <td className="py-2 pr-3">
                          {r.resolved ? (
                            <span className="text-b4l-green-dark">
                              ✓{' '}
                              {r.resolvedAt
                                ? new Date(r.resolvedAt).toLocaleDateString('en-GB')
                                : ''}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          {!r.resolved && r.status !== 'ok' && canRun && (
                            <Button
                              variant="secondary"
                              onClick={() => resolveMutation.mutate(r.id)}
                              loading={resolveMutation.isPending}
                            >
                              Rezolvă
                            </Button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <ul className="space-y-2 md:hidden">
              {data.results.map((r) => {
                const info = STATUS_LABELS[r.status] ?? {
                  label: r.status,
                  color: 'bg-b4l-bg-soft',
                }
                return (
                  <li
                    key={r.id}
                    className="rounded border border-b4l-border bg-b4l-bg-soft p-3 text-xs"
                  >
                    <div className="mb-2 flex items-center gap-2">
                      <span
                        className={`inline-block h-2 w-2 shrink-0 rounded-full ${
                          SEVERITY_DOT[r.severity] ?? 'bg-b4l-text-muted'
                        }`}
                      />
                      <span className={`rounded px-2 py-0.5 ${info.color}`}>{info.label}</span>
                      <span className="ml-auto text-[10px] uppercase text-b4l-text-muted">
                        {r.sourceType}
                      </span>
                    </div>
                    <div className="space-y-1">
                      <div>
                        <span className="text-[10px] text-b4l-text-muted">Doc SPV: </span>
                        <span className="font-mono break-all">{r.sourceRef}</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-b4l-text-muted">EA ref: </span>
                        <span className="font-mono break-all">{r.eaRef ?? '—'}</span>
                      </div>
                      {r.diffSummary && (
                        <div>
                          <span className="text-[10px] text-b4l-text-muted">Detalii: </span>
                          <span>{r.diffSummary}</span>
                        </div>
                      )}
                    </div>
                    {(r.resolved || (!r.resolved && r.status !== 'ok' && canRun)) && (
                      <div className="mt-2 flex items-center justify-between border-t border-b4l-border pt-2">
                        {r.resolved ? (
                          <span className="text-b4l-green-dark">
                            ✓ Rezolvat{' '}
                            {r.resolvedAt ? new Date(r.resolvedAt).toLocaleDateString('en-GB') : ''}
                          </span>
                        ) : (
                          <span className="text-b4l-text-muted">Nerezolvat</span>
                        )}
                        {!r.resolved && r.status !== 'ok' && canRun && (
                          <Button
                            variant="secondary"
                            onClick={() => resolveMutation.mutate(r.id)}
                            loading={resolveMutation.isPending}
                          >
                            Rezolvă
                          </Button>
                        )}
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          </>
        )}
      </Card>
    </div>
  )
}
